import { useCallback, useEffect, useState } from 'react';
import { getEventOpeningTime } from '@/utils';

export function useNflLobbyPopups({
	user,
	lobbyId,
	lobbyResponse,
	eventResponse,
}: {
	user: any;
	lobbyId: any;
	lobbyResponse: any;
	eventResponse: any;
}) {
	const eventStartDate = getEventOpeningTime(eventResponse)?.getTime() ?? null;

	// Open Lobby NFL Popup - shown once when lobby opens
	const [showOpenLobbyNflPopup, setShowOpenLobbyNflPopup] = useState(false);

	const openLobbyNflPopupKey =
		user?.id && lobbyId ? `open_lobby_nfl_popup_shown_${user.id}_${lobbyId}` : null;

	useEffect(() => {
		if (lobbyResponse?.show_mid_game_popup_first && openLobbyNflPopupKey) {
			const alreadyShown = localStorage.getItem(openLobbyNflPopupKey);
			const now = Date.now();
			const isSecondTriggered =
				lobbyResponse?.show_mid_game_popup_second && eventStartDate && now >= eventStartDate;

			if (!alreadyShown) {
				if (isSecondTriggered) {
					// Mark first as shown and NEVER show it if we're already past event start
					localStorage.setItem(openLobbyNflPopupKey, '1');
				} else {
					setShowOpenLobbyNflPopup(true);
				}
			}
		}
	}, [
		lobbyResponse?.show_mid_game_popup_first,
		lobbyResponse?.show_mid_game_popup_second,
		openLobbyNflPopupKey,
		eventStartDate,
	]);

	const handleCloseOpenLobbyNflPopup = useCallback(
		(open: boolean) => {
			setShowOpenLobbyNflPopup(open);
			if (!open && openLobbyNflPopupKey) {
				localStorage.setItem(openLobbyNflPopupKey, '1');
			}
		},
		[openLobbyNflPopupKey]
	);

	// Event Start NFL Popup - shown once when event starts
	const [showEventStartNflPopup, setShowEventStartNflPopup] = useState(false);

	const eventStartNflPopupKey =
		user?.id && lobbyId ? `event_start_nfl_popup_shown_${user.id}_${lobbyId}` : null;

	const handleCloseEventStartNflPopup = useCallback(
		(open: boolean) => {
			setShowEventStartNflPopup(open);
			if (!open && eventStartNflPopupKey) {
				localStorage.setItem(eventStartNflPopupKey, '1');
			}
		},
		[eventStartNflPopupKey]
	);

	// Event Start NFL Popup - real-time kickoff trigger
	useEffect(() => {
		if (!eventStartDate || !lobbyResponse?.show_mid_game_popup_second) return;

		const checkAndShow = () => {
			const now = Date.now();
			const alreadyShown = eventStartNflPopupKey
				? localStorage.getItem(eventStartNflPopupKey)
				: null;

			if (!alreadyShown && now >= eventStartDate) {
				setShowEventStartNflPopup(true);

				// Suppress/Mark first popup as shown
				setShowOpenLobbyNflPopup(false);
				if (openLobbyNflPopupKey && !localStorage.getItem(openLobbyNflPopupKey)) {
					localStorage.setItem(openLobbyNflPopupKey, '1');
				}

				return true;
			}
			return false;
		};

		// Check immediately upon entry
		if (checkAndShow()) return;

		// Poll until we hit the start time
		const id = setInterval(() => {
			if (checkAndShow()) {
				clearInterval(id);
			}
		}, 250);

		return () => clearInterval(id);
	}, [
		eventStartDate,
		lobbyResponse?.show_mid_game_popup_second,
		eventStartNflPopupKey,
		openLobbyNflPopupKey,
	]);

	return {
		eventStartDate,
		showOpenLobbyNflPopup,
		handleCloseOpenLobbyNflPopup,
		showEventStartNflPopup,
		handleCloseEventStartNflPopup,
	};
}
